import { useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AppIcon } from '../ui/icon'
import { mdiPaperclip, mdiUpload } from '@mdi/js'
import { EmptyState } from '../ui/empty-state'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { apiBaseUrl } from '../../api/client'
import { useDebounce } from '../../hooks/useDebounce'
import { useDocumentUrlResolver } from '../../hooks/useDocumentUrlResolver'
import { SECTION_ICONS } from '../../lib/section-icons'

export interface DocLite {
  uid: string
  name: string
  thumbnailUrl?: string
  /** The resolved audience (`internal` | `shared`) the list endpoint stamps. */
  effective_visibility?: string
}

export interface AttachDocumentControlLabels {
  attachExisting: string
  upload: string
  uploading: string
  pickDocumentTitle: string
  searchPlaceholder: string
  noDocumentsFound: string
  alreadyAttached: string
  /** Heading over the org-wide matches shown under the asset's own documents. */
  otherDocuments?: string
}

export interface AttachDocumentControlProps {
  org: string
  /**
   * Scopes the "browse existing" list to one asset's documents — matches how
   * interventions and expenses are always attached to a single asset. Omit
   * for hosts without one owning asset (e.g. a contract can span many
   * assets); the picker then falls back to a debounced server-side name
   * search across the whole org instead of an asset-scoped client filter.
   */
  assetUid?: string
  /** Documents already linked to the host entity — disabled + labelled "already attached" in the list. */
  linkedUids?: Set<string>
  /** Prefixes every data-testid this control renders, e.g. "intervention-doc" -> "intervention-doc-pick-btn". */
  testidPrefix: string
  /**
   * Set false to render only the "attach existing" action — used where the
   * host page already has its own upload affordance (e.g. ContractDocuments'
   * drag-and-drop dialog) and only needs the shared picker added next to it.
   */
  showUpload?: boolean
  /** asset_uid sent with the upload's multipart form; defaults to `assetUid`. Set explicitly if the host's owning asset differs from the picker's scope. */
  uploadAssetUid?: string
  labels: AttachDocumentControlLabels
  /** Only list documents whose type carries this flag (e.g. `visit_report`). */
  documentTypeFlag?: string
  /**
   * With `documentTypeFlag`, only list documents filed on one of these assets
   * (a contract covers several). Ignored when `assetUid` is set.
   */
  assetUids?: string[]
  /**
   * Called once a new file has been uploaded and the document record
   * created — the host decides how to associate it (create a document_link,
   * call a dedicated attach endpoint, set a document_uid column, …).
   */
  onUploaded: (documentUid: string) => void
  /** Called after picking an existing document from the dialog. */
  onSelect: (documentUid: string) => void
  className?: string
}

/**
 * Shared "attach a document" UX: an "Attach existing" action (opens a
 * searchable picker dialog that also resolves a pasted document URL/UID via
 * `useDocumentUrlResolver`) plus an optional "Upload" action. Used by
 * intervention, contract, and expense document sections so the paste-URL
 * affordance and picker behavior stay identical across all three (see spec
 * 2026-07-24-01-shared-attach-document-ui.md).
 */
export function AttachDocumentControl({
  org,
  assetUid,
  linkedUids,
  testidPrefix,
  showUpload = true,
  uploadAssetUid,
  labels,
  documentTypeFlag,
  assetUids,
  onUploaded,
  onSelect,
  className,
}: AttachDocumentControlProps) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const onUpload = async (file: File) => {
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const scopedAsset = uploadAssetUid ?? assetUid
      if (scopedAsset) form.append('asset_uid', scopedAsset)
      const token = localStorage.getItem('token')
      const res = await fetch(`${apiBaseUrl}/api/v1/organizations/${org}/documents`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      })
      if (res.ok) {
        const doc = await res.json()
        if (doc?.uid) onUploaded(doc.uid)
      }
    } finally {
      setUploading(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  return (
    <div className={className ?? 'flex flex-wrap gap-2'}>
      <Button
        size="sm"
        variant="outline"
        data-testid={`${testidPrefix}-pick-btn`}
        onClick={() => setPickerOpen(true)}
      >
        <AppIcon path={mdiPaperclip} size="sm" className="mr-1" />
        {labels.attachExisting}
      </Button>
      {showUpload && (
        <>
          <Button
            size="sm"
            variant="outline"
            data-testid={`${testidPrefix}-upload-btn`}
            disabled={uploading}
            onClick={() => fileInput.current?.click()}
          >
            <AppIcon path={mdiUpload} size="sm" className="mr-1" />
            {uploading ? labels.uploading : labels.upload}
          </Button>
          <input
            ref={fileInput}
            type="file"
            className="hidden"
            data-testid={`${testidPrefix}-file-input`}
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) onUpload(f)
            }}
          />
        </>
      )}
      {pickerOpen && (
        <DocumentPickerDialog
          org={org}
          assetUid={assetUid}
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          linkedUids={linkedUids ?? EMPTY_SET}
          testidPrefix={testidPrefix}
          labels={labels}
          documentTypeFlag={documentTypeFlag}
          assetUids={assetUids}
          onPick={(docUid) => {
            onSelect(docUid)
            setPickerOpen(false)
          }}
        />
      )}
    </div>
  )
}

const EMPTY_SET: Set<string> = new Set()

/**
 * The picker on its own, for hosts that open it from their own affordance
 * (the plan viewer's drop zone and "+" strip, spec 2026-09-05-02) rather
 * than through the control's "Attach existing" button.
 */
export function DocumentPickerDialog({
  org,
  assetUid,
  open,
  onOpenChange,
  linkedUids,
  testidPrefix,
  labels,
  onPick,
  rowHint,
  documentTypeFlag,
  assetUids,
}: {
  org: string
  assetUid?: string
  open: boolean
  onOpenChange: (o: boolean) => void
  linkedUids: Set<string>
  testidPrefix: string
  labels: AttachDocumentControlLabels
  /** `doc` is the picked row, for hosts that need more than its uid. */
  onPick: (docUid: string, doc: DocLite) => void
  /** Extra marker rendered at the end of a row (e.g. an "internal" badge). */
  rowHint?: (doc: DocLite) => ReactNode
  /** Only list documents whose type carries this flag. */
  documentTypeFlag?: string
  /** With `documentTypeFlag`: keep the documents filed on one of these assets. */
  assetUids?: string[]
}) {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const resolver = useDocumentUrlResolver(org, search, open)

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ['attach-doc-picker', org, assetUid, documentTypeFlag, assetUids, assetUid ? undefined : debouncedSearch],
    queryFn: async () => {
      const token = localStorage.getItem('token')
      const params = new URLSearchParams({ limit: assetUid || documentTypeFlag ? '200' : '50' })
      if (assetUid) {
        params.set('asset_uid', assetUid)
      } else if (debouncedSearch.trim()) {
        params.set('q', debouncedSearch.trim())
      }
      if (documentTypeFlag) {
        params.set('document_type_flag', documentTypeFlag)
        if (assetUids) params.set('include', 'asset_uid')
      }
      const res = await fetch(
        `${apiBaseUrl}/api/v1/organizations/${org}/documents?${params.toString()}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      )
      if (!res.ok) return [] as DocLite[]
      const body = await res.json()
      const rows = (body.data || []) as (DocLite & { asset_uid?: string | null })[]
      if (documentTypeFlag && assetUids && !assetUid) {
        const covered = new Set(assetUids)
        return rows.filter((d) => d.asset_uid && covered.has(d.asset_uid)) as DocLite[]
      }
      return rows as DocLite[]
    },
    enabled: open && !!org,
  })

  // An asset-scoped picker still searches the whole organization as the user
  // types: the document they mean is often filed elsewhere — the floor's plan
  // when attaching to a unit, a sibling's diagnostic. The asset's own matches
  // come first, the rest under their own heading.
  const orgSearch = debouncedSearch.trim()
  const { data: orgDocs = [], isLoading: orgLoading } = useQuery({
    queryKey: ['attach-doc-picker-org', org, orgSearch],
    queryFn: async () => {
      const token = localStorage.getItem('token')
      const params = new URLSearchParams({ limit: '50', q: orgSearch })
      const res = await fetch(
        `${apiBaseUrl}/api/v1/organizations/${org}/documents?${params.toString()}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      )
      if (!res.ok) return [] as DocLite[]
      const body = await res.json()
      return (body.data || []) as DocLite[]
    },
    enabled: open && !!org && !!assetUid && !documentTypeFlag && orgSearch.length > 0 && !resolver.isPastedReference,
  })

  const filtered = useMemo(() => {
    if (resolver.isPastedReference) return []
    if (!assetUid && !documentTypeFlag) return docs // already server-filtered by `q`
    const q = search.trim().toLowerCase()
    return q ? docs.filter((d) => (d.name || '').toLowerCase().includes(q)) : docs
  }, [docs, search, resolver.isPastedReference, assetUid, documentTypeFlag])
  const others = useMemo(() => {
    if (!assetUid || !orgSearch || resolver.isPastedReference) return []
    const own = new Set(docs.map((d) => d.uid))
    return orgDocs.filter((d) => !own.has(d.uid))
  }, [assetUid, orgSearch, resolver.isPastedReference, docs, orgDocs])
  const searching = isLoading || (orgLoading && !!assetUid && orgSearch.length > 0)

  const renderRow = (doc: DocLite) => {
    const already = linkedUids.has(doc.uid)
    return (
      <button
        key={doc.uid}
        type="button"
        disabled={already}
        onClick={() => onPick(doc.uid, doc)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent/50 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid={`${testidPrefix}-picker-option-${doc.uid}`}
      >
        {doc.thumbnailUrl ? (
          <img
            src={doc.thumbnailUrl}
            alt=""
            className="h-7 w-7 flex-shrink-0 rounded object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none' }}
          />
        ) : (
          <AppIcon tone="muted" path={SECTION_ICONS.documents} size="sm" className="flex-shrink-0" />
        )}
        <span className="flex-1 truncate">{doc.name}</span>
        {rowHint?.(doc)}
        {already && <span className="text-xs text-muted-foreground">{labels.alreadyAttached}</span>}
      </button>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={`${testidPrefix}-picker-dialog`}>
        <DialogHeader>
          <DialogTitle>{labels.pickDocumentTitle}</DialogTitle>
        </DialogHeader>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={labels.searchPlaceholder}
          data-testid={`${testidPrefix}-picker-search`}
        />
        <div className="max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border">
          {resolver.isPastedReference ? (
            resolver.isLoading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">…</div>
            ) : resolver.document ? (
              renderRow(resolver.document)
            ) : (
              <EmptyState
                variant="inline"
                live
                title={labels.noDocumentsFound}
                data-testid={`${testidPrefix}-picker-empty`}
              />
            )
          ) : filtered.length === 0 && others.length === 0 ? (
            searching ? (
              <div className="p-4 text-center text-sm text-muted-foreground">…</div>
            ) : (
              <EmptyState
                variant="inline"
                live
                title={labels.noDocumentsFound}
                data-testid={`${testidPrefix}-picker-empty`}
              />
            )
          ) : (
            <>
              {filtered.map(renderRow)}
              {others.length > 0 && (
                <div
                  className="bg-muted/50 px-3 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground"
                  data-testid={`${testidPrefix}-picker-others`}
                >
                  {labels.otherDocuments ?? 'Other documents'}
                </div>
              )}
              {others.map(renderRow)}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
